import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  User, Star, MapPin, Calendar, Trophy, Award, 
  BookOpen, Coffee, CheckCircle2, Clock, Camera,
  ArrowRight, Mail, Lock, Eye, EyeOff, AlertCircle, Check, LogOut,
  Settings, History, Edit3, Upload, X, Shield, Sparkles, Gift, Trash2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { toast } from "sonner";
import PasswordStrengthMeter, { isPasswordStrong } from "../components/PasswordStrengthMeter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContributorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlVoucherCode = searchParams.get("code");
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [contributor, setContributor] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Pending voucher from URL
  const [pendingVoucher, setPendingVoucher] = useState(null);
  const [pendingVoucherError, setPendingVoucherError] = useState(null);
  
  // Auth states: "lookup", "login", "set-password", "profile"
  const [authState, setAuthState] = useState("lookup");
  
  // Profile tab states
  const [activeTab, setActiveTab] = useState("overview");
  
  // Settings states
  const [presetAvatars, setPresetAvatars] = useState([]);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [editingUsername, setEditingUsername] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [redeemingVoucher, setRedeemingVoucher] = useState(false);
  
  // Forgot password states
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetToken, setResetToken] = useState(searchParams.get("reset") || "");
  const [resetPassword, setResetPassword] = useState("");
  const [confirmResetPassword, setConfirmResetPassword] = useState("");
  
  // Delete account states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Terms and conditions state
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Handle forgot password request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contributors/forgot-password`, { email: forgotPasswordEmail });
      toast.success("If an account exists, you'll receive a reset link shortly.");
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (err) {
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password reset with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassword !== confirmResetPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!isPasswordStrong(resetPassword)) {
      toast.error("Please create a stronger password");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contributors/reset-password`, {
        token: resetToken,
        new_password: resetPassword
      });
      toast.success("Password reset successfully! You can now login.");
      setResetToken("");
      setResetPassword("");
      setConfirmResetPassword("");
      setSearchParams({});
      setAuthState("login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };
  
  // Check for reset token on mount
  useEffect(() => {
    const token = searchParams.get("reset");
    if (token) {
      setResetToken(token);
      setAuthState("reset-password");
    }
  }, [searchParams]);
  
  // Redeem voucher code for bonus points
  const redeemVoucher = async () => {
    if (!voucherCode.trim() || !contributor?.id) return;
    setRedeemingVoucher(true);
    try {
      const res = await axios.post(`${API}/vouchers/redeem`, {
        code: voucherCode.trim(),
        contributor_id: contributor.id
      });
      toast.success(res.data.message);
      setVoucherCode("");
      // Refresh contributor data to show new points
      const contribRes = await axios.get(`${API}/contributors/${contributor.id}`);
      setContributor(contribRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid voucher code");
    } finally {
      setRedeemingVoucher(false);
    }
  };
  
  const loadFullProfile = useCallback(async (contributorId, redeemPendingVoucher = false) => {
    setLoading(true);
    try {
      const [profileRes, historyRes] = await Promise.all([
        axios.get(`${API}/contributors/${contributorId}/profile`),
        axios.get(`${API}/contributors/${contributorId}/points-history?limit=20`)
      ]);
      
      setContributor(profileRes.data);
      setPointsHistory(historyRes.data.history || []);
      setNewUsername(profileRes.data.username || "");
      
      // Also get contributions
      if (profileRes.data.email) {
        const reviewsRes = await axios.get(`${API}/reviews/by-email/${encodeURIComponent(profileRes.data.email)}`);
        setContributions(reviewsRes.data || []);
      }
      
      // Redeem pending voucher if specified
      if (redeemPendingVoucher && pendingVoucher && urlVoucherCode) {
        try {
          const voucherRes = await axios.post(`${API}/vouchers/redeem`, {
            code: urlVoucherCode,
            contributor_id: contributorId,
            contributor_email: profileRes.data.email
          });
          toast.success(voucherRes.data.message || `+${voucherRes.data.points_awarded} points!`);
          setPendingVoucher(null);
          // Clear URL param
          setSearchParams({});
          // Refresh history
          const newHistory = await axios.get(`${API}/contributors/${contributorId}/points-history?limit=20`);
          setPointsHistory(newHistory.data.history || []);
          // Refresh profile to get updated points
          const newProfile = await axios.get(`${API}/contributors/${contributorId}/profile`);
          setContributor(newProfile.data);
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (detail && typeof detail === 'string') {
            toast.error(detail);
          }
        }
      }
      
      setAuthState("profile");
    } catch (err) {
      console.error("Failed to load profile:", err);
      sessionStorage.removeItem("contributor_id");
      sessionStorage.removeItem("contributor_email");
      setAuthState("lookup");
    } finally {
      setLoading(false);
    }
  }, [pendingVoucher, urlVoucherCode, setSearchParams]);
  
  // Check if user is already logged in (check localStorage first for persistent login, then sessionStorage)
  useEffect(() => {
    // Try localStorage first (persistent Google auth)
    let savedId = localStorage.getItem("contributor_id");
    let savedEmail = localStorage.getItem("contributor_email");
    
    // Fall back to sessionStorage
    if (!savedId || !savedEmail) {
      savedId = sessionStorage.getItem("contributor_id");
      savedEmail = sessionStorage.getItem("contributor_email");
    }
    
    if (savedId && savedEmail) {
      setEmail(savedEmail);
      loadFullProfile(savedId);
      
      // Sync to sessionStorage if loaded from localStorage
      if (!sessionStorage.getItem("contributor_id")) {
        sessionStorage.setItem("contributor_id", savedId);
        sessionStorage.setItem("contributor_email", savedEmail);
      }
    }
    
    // Load preset avatars
    axios.get(`${API}/gamification/preset-avatars`)
      .then(res => setPresetAvatars(res.data))
      .catch(() => {});
  }, [loadFullProfile]);

  // Validate voucher code from URL
  useEffect(() => {
    if (!urlVoucherCode) return;
    
    const validateUrlVoucher = async () => {
      try {
        const response = await axios.post(`${API}/vouchers/validate`, { code: urlVoucherCode });
        if (response.data.voucher_type === "contributor_points") {
          setPendingVoucher(response.data);
          // Pre-fill the voucher input
          setVoucherCode(urlVoucherCode);
        } else {
          // Not a contributor voucher, redirect to claim
          window.location.href = `/claim?promo=${urlVoucherCode}`;
        }
      } catch (err) {
        setPendingVoucherError(err.response?.data?.detail || "Invalid voucher code");
      }
    };
    
    validateUrlVoucher();
  }, [urlVoucherCode]);

  const handleEmailLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/contributors/check-email/${encodeURIComponent(email.trim())}`);
      
      if (!response.data.found) {
        toast.info("No account found. Start contributing by submitting a venue or review!");
        setAuthState("lookup");
        return;
      }
      
      if (response.data.has_password) {
        setAuthState("login");
      } else {
        setAuthState("set-password");
        toast.info("Welcome! Please create a password to secure your account.");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info("No contributions found for this email. Start contributing to create your profile!");
      } else {
        setError("Error checking account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/contributors/login`, {
        email: email.trim(),
        password
      });

      if (response.data.success) {
        sessionStorage.setItem("contributor_id", response.data.contributor_id);
        sessionStorage.setItem("contributor_email", response.data.email);
        toast.success(`Welcome back, ${response.data.name}!`);
        // Pass true to redeem pending voucher after login
        await loadFullProfile(response.data.contributor_id, true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Password not set. Please set up your password first.") {
        setAuthState("set-password");
        toast.info("Please set up your password to continue");
      } else {
        setError(detail || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (!isPasswordStrong(password)) {
      setError("Please create a stronger password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/contributors/set-password`, {
        email: email.trim(),
        password,
        confirm_password: confirmPassword
      });

      toast.success("Password set successfully!");
      
      const loginRes = await axios.post(`${API}/contributors/login`, {
        email: email.trim(),
        password
      });

      if (loginRes.data.success) {
        sessionStorage.setItem("contributor_id", loginRes.data.contributor_id);
        sessionStorage.setItem("contributor_email", loginRes.data.email);
        // Pass true to redeem pending voucher after registration
        await loadFullProfile(loginRes.data.contributor_id, true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to set password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Try both storage locations for session token
      const sessionToken = localStorage.getItem("contributor_session_token") || sessionStorage.getItem("contributor_session_token");
      if (sessionToken) {
        await axios.post(`${API}/contributors/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
      }
    } catch (err) {
      // Expected: Logout may fail if session already expired
      console.debug('Logout request failed (session may have expired):', err.message);
    }
    // Clear from both sessionStorage and localStorage
    sessionStorage.removeItem("contributor_id");
    sessionStorage.removeItem("contributor_email");
    sessionStorage.removeItem("contributor_session_token");
    localStorage.removeItem("contributor_id");
    localStorage.removeItem("contributor_email");
    localStorage.removeItem("contributor_name");
    localStorage.removeItem("contributor_session_token");
    localStorage.removeItem("contributor_auth_provider");
    localStorage.removeItem("contributor_login_time");
    setContributor(null);
    setContributions([]);
    setPointsHistory([]);
    setPassword("");
    setConfirmPassword("");
    setAuthState("lookup");
    toast.success("Logged out successfully");
  };

  // Profile management functions
  const handleUsernameUpdate = async () => {
    if (!newUsername.trim()) {
      toast.error("Username cannot be empty");
      return;
    }
    
    try {
      await axios.post(`${API}/contributors/${contributor.id}/username`, {
        username: newUsername.trim()
      });
      
      setContributor(prev => ({ ...prev, username: newUsername.trim() }));
      setEditingUsername(false);
      toast.success("Username updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update username");
    }
  };

  const handleAvatarSelect = async (avatarId) => {
    try {
      await axios.post(`${API}/contributors/${contributor.id}/avatar`, {
        avatar_type: "preset",
        avatar: avatarId
      });
      
      setContributor(prev => ({
        ...prev,
        avatar: `/avatars/${avatarId}.svg`,
        avatar_type: "preset"
      }));
      setShowAvatarPicker(false);
      toast.success("Avatar updated!");
    } catch (err) {
      toast.error("Failed to update avatar");
    }
  };

  const handleCustomAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 500000) {
      toast.error("Image too large (max 500KB)");
      return;
    }
    
    setUploadingAvatar(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        
        await axios.post(`${API}/contributors/${contributor.id}/avatar`, {
          avatar_type: "custom",
          avatar: base64
        });
        
        setContributor(prev => ({
          ...prev,
          avatar: base64,
          avatar_type: "custom"
        }));
        setShowAvatarPicker(false);
        toast.success("Avatar uploaded!");
        setUploadingAvatar(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Failed to upload avatar");
      setUploadingAvatar(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (!isPasswordStrong(newPassword)) {
      toast.error("Please create a stronger password");
      return;
    }
    
    try {
      await axios.post(`${API}/contributors/set-password`, {
        email: contributor.email,
        password: newPassword,
        confirm_password: confirmNewPassword
      });
      
      toast.success("Password changed successfully!");
      setShowPasswordChange(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/contributors/${contributor.id}/account`, {
        data: { confirmation: "DELETE" }
      });
      
      toast.success("Your account has been deleted");
      sessionStorage.removeItem("contributor_id");
      sessionStorage.removeItem("contributor_email");
      sessionStorage.removeItem("contributor_session_token");
      setContributor(null);
      setAuthState("lookup");
      setShowDeleteConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeletingAccount(false);
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  // Render auth forms (lookup, login, set-password)
  const renderAuthForms = () => (
    <>
      {/* Pending Voucher Banner */}
      {pendingVoucher && authState !== "profile" && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl text-white"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold">+{pendingVoucher.value} Bonus Points!</h3>
              <p className="text-sm text-white/80">
                Sign in or register to claim your bonus points from code <strong>{urlVoucherCode}</strong>
              </p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Voucher Error Banner */}
      {pendingVoucherError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm text-red-700">
                Invalid voucher code: <strong>{urlVoucherCode}</strong>
              </p>
              <p className="text-xs text-red-500 mt-1">{pendingVoucherError}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* Email Lookup Form */}
        {authState === "lookup" && (
          <motion.form 
            key="lookup"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={handleEmailLookup} 
            className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="space-y-4">
              {/* Google Sign In - Primary Option */}
              <button
                type="button"
                onClick={() => {
                  // Store pending voucher code for redemption after auth
                  if (urlVoucherCode) {
                    sessionStorage.setItem('pending_voucher_code', urlVoucherCode);
                  }
                  sessionStorage.setItem('auth_return_url', window.location.pathname + window.location.search);
                  const redirectUrl = window.location.origin + '/contributor/auth/callback';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-3"
                data-testid="google-signin-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              
              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500">or use email</span>
                </div>
              </div>
              
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="contributor-email-input"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors flex items-center justify-center gap-2"
                data-testid="continue-btn"
              >
                {loading ? "Checking..." : "Continue with Email"}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </motion.form>
        )}

        {/* Login Form */}
        {authState === "login" && (
          <motion.form 
            key="login"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={handleLogin} 
            className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Signing in as <strong>{email}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="contributor-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors"
                data-testid="login-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setAuthState("lookup"); setError(""); setPassword(""); }}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  ← Use a different email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordEmail(email);
                    setShowForgotPassword(true);
                  }}
                  className="text-brand hover:text-brand-dark text-sm font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            </div>
          </motion.form>
        )}

        {/* Reset Password Form (from email link) */}
        {authState === "reset-password" && (
          <motion.form 
            key="reset-password"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={handleResetPassword} 
            className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Reset your password</strong> - Enter a new password below
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="reset-new-password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <PasswordStrengthMeter password={resetPassword} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmResetPassword}
                    onChange={(e) => setConfirmResetPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="reset-confirm-password"
                    required
                    minLength={8}
                  />
                </div>
                {resetPassword && confirmResetPassword && (
                  <p className={`text-xs mt-1.5 flex items-center gap-1 ${
                    resetPassword === confirmResetPassword ? "text-green-600" : "text-red-600"
                  }`}>
                    {resetPassword === confirmResetPassword ? (
                      <><Check className="w-3 h-3" /> Passwords match</>
                    ) : (
                      <><AlertCircle className="w-3 h-3" /> Passwords do not match</>
                    )}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !isPasswordStrong(resetPassword) || resetPassword !== confirmResetPassword}
                className="w-full px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reset-password-btn"
              >
                {loading ? "Resetting password..." : "Reset Password"}
              </button>

              <button
                type="button"
                onClick={() => { setAuthState("lookup"); setResetToken(""); setSearchParams({}); }}
                className="w-full text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Back to login
              </button>
            </div>
          </motion.form>
        )}

        {/* Set Password Form */}
        {authState === "set-password" && (
          <motion.form 
            key="set-password"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={handleSetPassword} 
            className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>First time login?</strong> Create a password to secure your account for <strong>{email}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Create Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="new-password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <PasswordStrengthMeter password={password} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    data-testid="confirm-password"
                    required
                    minLength={8}
                  />
                </div>
                {password && confirmPassword && (
                  <p className={`text-xs mt-1.5 flex items-center gap-1 ${
                    password === confirmPassword ? "text-green-600" : "text-red-600"
                  }`}>
                    {password === confirmPassword ? (
                      <><Check className="w-3 h-3" /> Passwords match</>
                    ) : (
                      <><AlertCircle className="w-3 h-3" /> Passwords do not match</>
                    )}
                  </p>
                )}
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
                  data-testid="accept-terms-checkbox"
                />
                <label htmlFor="accept-terms" className="text-sm text-gray-600">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" className="text-brand hover:underline">Terms of Service</a>
                  {" "}and{" "}
                  <a href="/contributor-terms" target="_blank" className="text-brand hover:underline">Contributor Agreement</a>,
                  and acknowledge the{" "}
                  <a href="/privacy" target="_blank" className="text-brand hover:underline">Privacy Policy</a>.
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !isPasswordStrong(password) || password !== confirmPassword || !acceptedTerms}
                className="w-full px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="set-password-btn"
              >
                {loading ? "Setting password..." : "Set Password & Continue"}
              </button>

              <button
                type="button"
                onClick={() => { setAuthState("lookup"); setError(""); setPassword(""); setConfirmPassword(""); }}
                className="w-full text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Use a different email
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Info Card */}
      {authState === "lookup" && (
        <div className="bg-gradient-to-br from-brand/5 to-indigo-50 rounded-2xl p-6 border border-brand/10">
          <h3 className="font-semibold text-gray-900 mb-3">Why contribute?</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-brand mt-0.5" />
              Help fellow remote workers find great workspaces
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-brand mt-0.5" />
              Earn XP points and climb the leaderboard
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-brand mt-0.5" />
              Unlock badges for your contributions
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-brand mt-0.5" />
              Win monthly gift cards & yearly trophy
            </li>
          </ul>
        </div>
      )}
    </>
  );

  // Render profile dashboard
  const renderProfile = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Profile Header Card */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            {/* Avatar with edit button */}
            <div className="relative">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-brand to-indigo-600 flex items-center justify-center">
                {contributor.avatar ? (
                  <img src={contributor.avatar} alt={contributor.username} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-white">
                    {(contributor.username || contributor.name)?.[0]?.toUpperCase() || "C"}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowAvatarPicker(true)}
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-brand text-white rounded-full flex items-center justify-center shadow-lg hover:bg-brand-dark transition-colors"
                data-testid="edit-avatar-btn"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>
            
            <div>
              {/* Username with edit */}
              <div className="flex items-center gap-2">
                {editingUsername ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      className="px-3 py-1 border border-gray-200 rounded-lg text-lg font-bold focus:ring-2 focus:ring-brand/20"
                      placeholder="Username"
                      maxLength={20}
                    />
                    <button onClick={handleUsernameUpdate} className="p-1 text-green-600 hover:bg-green-50 rounded">
                      <Check className="w-5 h-5" />
                    </button>
                    <button onClick={() => { setEditingUsername(false); setNewUsername(contributor.username || ""); }} className="p-1 text-gray-400 hover:bg-gray-50 rounded">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-gray-900">
                      {contributor.username || contributor.name || "Contributor"}
                    </h2>
                    <button 
                      onClick={() => setEditingUsername(true)}
                      className="p-1 text-gray-400 hover:text-brand hover:bg-brand/5 rounded"
                      data-testid="edit-username-btn"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              <p className="text-gray-500 text-sm">{contributor.email}</p>
              {contributor.level && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                  <Trophy className="w-3 h-3" />
                  Level {contributor.level}: {contributor.level_name}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowPasswordChange(true)}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-brand hover:bg-brand/5 rounded-lg transition-colors text-sm"
              data-testid="change-password-btn"
            >
              <Shield className="w-4 h-4" />
              Password
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {/* Points Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gradient-to-br from-brand/5 to-brand/10 rounded-xl">
            <Sparkles className="w-6 h-6 mx-auto text-brand mb-1" />
            <p className="text-2xl font-bold text-gray-900">{contributor.total_xp || contributor.stats?.total_xp || 0}</p>
            <p className="text-xs text-gray-500">Total Points</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <BookOpen className="w-6 h-6 mx-auto text-blue-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{contributor.stats?.total_reviews || contributions.length || 0}</p>
            <p className="text-xs text-gray-500">Reviews</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <MapPin className="w-6 h-6 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{contributor.stats?.total_submissions || 0}</p>
            <p className="text-xs text-gray-500">Venues Added</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <Star className="w-6 h-6 mx-auto text-amber-500 mb-1" />
            <p className="text-2xl font-bold text-gray-900">{contributor.stats?.helpful_votes_received || 0}</p>
            <p className="text-xs text-gray-500">Helpful Votes</p>
          </div>
        </div>

        {/* Badges */}
        {contributor.badges && contributor.badges.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Badges Earned</h3>
            <div className="flex flex-wrap gap-2">
              {contributor.badges.map((badge) => (
                <span key={badge} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                  <Award className="w-4 h-4" />
                  {badge.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
        {[
          { id: "overview", label: "Overview", icon: User },
          { id: "history", label: "Points History", icon: History },
          { id: "contributions", label: "Contributions", icon: BookOpen },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === tab.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <motion.div
            key="overview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/add-location"
                className="flex items-center gap-4 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
              >
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Add a Venue</p>
                  <p className="text-sm text-gray-500">Earn 25 points</p>
                </div>
              </Link>
              <Link
                to="/browse"
                className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Write a Review</p>
                  <p className="text-sm text-gray-500">Earn 10 points</p>
                </div>
              </Link>
              <Link
                to="/community"
                className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors"
              >
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">View Leaderboard</p>
                  <p className="text-sm text-gray-500">See your ranking</p>
                </div>
              </Link>
              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Member Since</p>
                  <p className="text-sm text-gray-500">{formatDate(contributor.created_at)}</p>
                </div>
              </div>
            </div>
            
            {/* Voucher Code Redemption */}
            <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-teal-600" />
                <h4 className="font-medium text-gray-900">Redeem Voucher Code</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Have a promo code? Enter it here to earn bonus points!
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter voucher code"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  className="flex-1 px-3 py-2 border border-teal-200 rounded-lg text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
                  data-testid="contributor-voucher-input"
                />
                <button
                  onClick={redeemVoucher}
                  disabled={!voucherCode.trim() || redeemingVoucher}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contributor-voucher-redeem"
                >
                  {redeemingVoucher ? "..." : "Redeem"}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "history" && (
          <motion.div
            key="history"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Points History</h3>
            {pointsHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No points earned yet</p>
                <p className="text-sm">Start contributing to earn points!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pointsHistory.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        item.points > 0 ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                      }`}>
                        {item.points > 0 ? "+" : ""}{item.points}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{item.reason}</p>
                        <p className="text-xs text-gray-500">{formatDate(item.created_at)}</p>
                      </div>
                    </div>
                    {item.reference_type && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                        {item.reference_type}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "contributions" && (
          <motion.div
            key="contributions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Your Contributions</h3>
            {contributions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No contributions yet</p>
                <Link to="/browse" className="text-brand hover:underline text-sm">Start reviewing venues</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {contributions.map((review) => (
                  <Link
                    key={review.id}
                    to={`/venue/${review.venue_id}`}
                    className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{review.venue_name || "Venue"}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center text-amber-500">
                            {[...Array(5)].map((_, i) => (
                              <Star key={`star-${review.id}-${i}`} className={`w-3 h-3 ${i < review.rating ? "fill-current" : "opacity-30"}`} />
                            ))}
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(review.created_at)}</span>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        review.status === "approved" ? "bg-green-100 text-green-700" :
                        review.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>
                        {review.status}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{review.comment}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  // Avatar Picker Modal
  const renderAvatarPicker = () => (
    <AnimatePresence>
      {showAvatarPicker && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowAvatarPicker(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Choose Your Avatar</h3>
              <button onClick={() => setShowAvatarPicker(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Upload Custom */}
            <div className="mb-6">
              <label className="block w-full p-4 border-2 border-dashed border-gray-200 rounded-xl text-center cursor-pointer hover:border-brand hover:bg-brand/5 transition-colors">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-700">Upload Custom Photo</p>
                <p className="text-xs text-gray-500">Max 500KB, JPG/PNG</p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleCustomAvatarUpload}
                  className="hidden"
                  disabled={uploadingAvatar}
                />
              </label>
            </div>

            {/* Preset Avatars by Category */}
            <div className="space-y-6">
              {["Hobbit", "Wizard", "Elf", "Dwarf", "Human", "Ent", "Creature"].map((category) => {
                const categoryAvatars = presetAvatars.filter(a => a.category === category);
                if (categoryAvatars.length === 0) return null;
                
                return (
                  <div key={category}>
                    <h4 className="text-sm font-medium text-gray-500 mb-3">{category}s</h4>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                      {categoryAvatars.map((avatar) => (
                        <button
                          key={avatar.id}
                          onClick={() => handleAvatarSelect(avatar.id)}
                          className={`relative p-2 rounded-xl border-2 transition-all hover:scale-105 ${
                            contributor.avatar === avatar.url
                              ? "border-brand bg-brand/5"
                              : "border-gray-100 hover:border-gray-200"
                          }`}
                        >
                          <img src={avatar.url} alt={avatar.name} className="w-full aspect-square rounded-lg" />
                          <p className="text-xs text-center mt-1 text-gray-600 truncate">{avatar.name}</p>
                          {contributor.avatar === avatar.url && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-brand rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Password Change Modal
  const renderPasswordChangeModal = () => (
    <AnimatePresence>
      {showPasswordChange && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPasswordChange(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Change Password</h3>
              <button onClick={() => setShowPasswordChange(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20"
                    required
                    minLength={8}
                  />
                </div>
                <PasswordStrengthMeter password={newPassword} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={!isPasswordStrong(newPassword) || newPassword !== confirmNewPassword}
                className="w-full px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50"
              >
                Update Password
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Forgot Password Modal
  const renderForgotPasswordModal = () => (
    <AnimatePresence>
      {showForgotPassword && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowForgotPassword(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Forgot Password</h3>
              <button onClick={() => setShowForgotPassword(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 text-sm mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20"
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !forgotPasswordEmail.trim()}
                className="w-full px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50"
                data-testid="send-reset-link-btn"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Delete Account Confirmation Modal
  const renderDeleteAccountModal = () => (
    <AnimatePresence>
      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-red-600">Delete Account</h3>
              <button onClick={() => setShowDeleteConfirm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 bg-red-50 rounded-xl mb-4">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This action cannot be undone. Your account, points, and contribution history will be permanently deleted.
              </p>
            </div>

            <p className="text-gray-600 text-sm mb-4">
              Type <strong>DELETE</strong> below to confirm account deletion:
            </p>

            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder="Type DELETE to confirm"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-200 focus:border-red-400 mb-4"
              data-testid="delete-confirm-input"
            />

            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== "DELETE" || deletingAccount}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-delete-btn"
              >
                {deletingAccount ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-surface relative">
      {/* Strider Logo Watermark */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.025] pointer-events-none z-0"
        style={{
          width: '700px',
          height: '700px',
          backgroundImage: 'url(/strider-logo-bg.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      <SEO 
        title={authState === "profile" ? "My Profile" : "Contributor Login"}
        description="Manage your Strider contributor profile, view your points, and customize your avatar."
        canonical="/contributor"
      />
      <Header />
      
      <main className="pt-20 pb-12 px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-brand" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {authState === "profile" ? "My Profile" : "Contributor Login"}
            </h1>
            <p className="text-gray-600">
              {authState === "lookup" && "Enter your email to access your contributions"}
              {authState === "login" && "Enter your password to sign in"}
              {authState === "set-password" && "Create a password to secure your account"}
              {authState === "reset-password" && "Create a new password for your account"}
              {authState === "profile" && "Manage your profile and track your contributions"}
            </p>
          </div>

          {/* Content */}
          {authState === "profile" && contributor ? renderProfile() : renderAuthForms()}
        </div>
      </main>

      {/* Modals */}
      {renderAvatarPicker()}
      {renderPasswordChangeModal()}
      {renderForgotPasswordModal()}
      {renderDeleteAccountModal()}

      <Footer />
    </div>
  );
}
